import { buildActorName, realActorName } from '../../services/actorName';

/**
 * Reine Logik des CSV-Projektimports (AdminCsvImportModal). Ausgelagert, weil
 * die Zuordnung des Koordinators eine Zugriffsentscheidung ist und deshalb
 * ohne DOM-Testumgebung prüfbar sein muss (kein jsdom im Projekt).
 */

/** Die Koordinatorenspalten einer Importzeile. */
export interface CsvCoordinatorRow {
    coordinator?: string;
    coordinatorEmail?: string;
}

/** Der Mensch, der den Import auslöst (Profilzeile). */
export interface ImportingUser {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
}

export interface ResolvedCoordinator {
    /** Name für Metadaten und Projektspalte — nie leer, wenn irgendetwas bekannt ist. */
    name: string;
    /**
     * `coordinatorId` NUR, wenn der Koordinator nachweislich der importierende
     * Nutzer ist. Sonst bewusst `undefined`.
     */
    id?: string;
}

const lower = (value?: string | null): string => (value || '').trim().toLowerCase();

/**
 * Wer ist Koordinator der importierten Zeile — und darf dessen id gesetzt werden?
 *
 * Der Import stempelte bisher ausnahmslos die id des importierenden Admins in
 * jedes angelegte Projekt, auch wenn die CSV namentlich einen ganz anderen
 * Menschen als Koordinator nannte. Folge: Der Admin galt in allen importierten
 * Projekten als Hauptverantwortlicher — inklusive echtem RLS-Zweig
 * (`coordinatorId = auth.uid()`, supabase/representatives_access.sql) — und
 * bekam sie fortan als „seine" Projekte vorgesetzt. Das ist die wahrscheinlichste
 * Quelle der Projektschwemme, über die der Betreiber berichtet.
 *
 * Die id ist eine Identitätsaussage und wird deshalb nur gesetzt, wenn die
 * Zeile diese Identität belegt:
 *   1. E-Mail-Spalte trifft den Importierenden  → eindeutig, gewinnt vor allem.
 *   2. E-Mail-Spalte nennt jemand anderen        → fremd, keine id (auch wenn
 *      der Name zufällig passt: die E-Mail ist das schärfere Merkmal).
 *   3. Keine Angabe in der Zeile                 → der Importierende trägt sich
 *      selbst ein (bisheriges Fallback-Verhalten, jetzt auch für die id gültig).
 *   4. Nur ein Name                              → id nur bei Namensgleichheit.
 *
 * Bleibt die id leer, steht der Name allein im Feld — genau wie bei den
 * Alt-Projekten, deren Koordinator nie eine id hatte. Der Zugriff des Admins
 * verengt sich dadurch nicht: `projects.user_id` bleibt der Importierende, und
 * die Rolle 'admin' sieht ohnehin alles (is_admin()).
 */
export const resolveImportCoordinator = (
    row: CsvCoordinatorRow,
    currentUser: ImportingUser | null | undefined
): ResolvedCoordinator => {
    const csvName = realActorName(row.coordinator);
    const csvMail = lower(row.coordinatorEmail);

    const ownName = buildActorName(currentUser);
    const ownMail = lower(currentUser?.email);
    const ownId = (currentUser?.id || '').trim() || undefined;

    // 1./2. Die E-Mail entscheidet, sobald die Zeile eine nennt.
    if (csvMail) {
        const isSelf = !!ownMail && csvMail === ownMail;
        return { name: csvName || (isSelf ? ownName : ''), id: isSelf ? ownId : undefined };
    }

    // 3. Leere Koordinatorenspalten: der Importierende ist der Koordinator.
    if (!csvName) return { name: ownName, id: ownId };

    // 4. Nur ein Name — id ausschließlich bei Namensgleichheit.
    const isSelf = !!ownName && csvName.toLowerCase() === ownName.toLowerCase();
    return { name: csvName, id: isSelf ? ownId : undefined };
};
